import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Layers, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import axios from 'axios';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface Schedule {
  _id: string;
  name: string;
}

interface Section {
  _id: string;
  name: string;
  description?: string;
  // A section can have multiple schedules
  schedules?: (Schedule | string)[];
  isActive: boolean;
}

const SectionsPage: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    schedules: string[];
  }>({
    name: '',
    description: '',
    schedules: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchSchedules = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/schedules`, axiosConfig);
      setSchedules(response.data.schedules || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, []);

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/sections`, axiosConfig);
      setSections(response.data.sections || []);
    } catch (error) {
      toast.error('Failed to fetch sections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSections();
    fetchSchedules();
  }, [fetchSections, fetchSchedules]);

  const handleOpenModal = (section?: Section) => {
    if (section) {
      setEditingSection(section);
      const scheduleIds: string[] = Array.isArray(section.schedules)
        ? section.schedules
            .map((s) =>
              typeof s === 'object' && s
                ? (s as Schedule)._id
                : typeof s === 'string'
                ? s
                : ''
            )
            .filter((id): id is string => Boolean(id))
        : [];

      setFormData({
        name: section.name,
        description: section.description || '',
        schedules: scheduleIds,
      });
    } else {
      setEditingSection(null);
      setFormData({ name: '', description: '', schedules: [] });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingSection(null);
    setFormData({ name: '', description: '', schedules: [] });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Section name is required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      if (editingSection) {
        await axios.put(`${API_URL}/sections/${editingSection._id}`, formData, axiosConfig);
        toast.success('Section updated successfully');
      } else {
        await axios.post(`${API_URL}/sections`, formData, axiosConfig);
        toast.success('Section created successfully');
      }
      handleCloseModal();
      fetchSections();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save section');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    try {
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/sections/${id}`, axiosConfig);
      toast.success('Section deleted successfully');
      fetchSections();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete section');
    }
  };

  const filteredSections = sections.filter(section =>
    section.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Name', accessor: (row: Section) => row.name },
    {
      header: 'Schedules',
      accessor: (row: Section) => {
        if (Array.isArray(row.schedules) && row.schedules.length > 0) {
          const names = row.schedules
            .map((s) =>
              typeof s === 'object' && s ? (s as Schedule).name : ''
            )
            .filter(Boolean);
          return names.length ? names.join(', ') : '-';
        }
        return '-';
      },
    },
    {
      header: 'Description',
      accessor: (row: Section) => (
        <div className="max-w-xs whitespace-normal break-words text-sm text-gray-700">
          {row.description || '-'}
        </div>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: Section) => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" icon={<Edit2 className="w-4 h-4" />} onClick={() => handleOpenModal(row)} />
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" icon={<Trash2 className="w-4 h-4" />} onClick={() => handleDelete(row._id)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sections</h1>
          <p className="mt-2 text-sm text-gray-600">Manage section information</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Section</Button>
      </motion.div>

      <Card padding="md">
        <Input placeholder="Search sections..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} icon={<Search className="w-5 h-5" />} fullWidth />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12"><LoadingSpinner size="lg" /></div>
        ) : filteredSections.length > 0 ? (
          <Table data={filteredSections} columns={columns} emptyMessage="No sections found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <Layers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No sections yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first section.</p>
            <div className="mt-6"><Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Section</Button></div>
          </div>
        )}
      </Card>

      <Modal isOpen={modalOpen} onClose={handleCloseModal} title={editingSection ? 'Edit Section' : 'Add New Section'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter section name" required fullWidth />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'var(--font-family)' }}>
              Schedules <span className="text-gray-500">(Optional, tick one or more)</span>
            </label>
            <div
              className="border border-gray-300 rounded-md px-3 py-2 bg-white max-h-56 overflow-y-auto space-y-1"
              style={{ fontFamily: 'var(--font-family)' }}
            >
              {schedules.map((schedule) => {
                const checked = formData.schedules.includes(schedule._id);
                return (
                  <label
                    key={schedule._id}
                    className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      checked={checked}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setFormData((prev) => {
                          const current = new Set(prev.schedules);
                          if (isChecked) {
                            current.add(schedule._id);
                          } else {
                            current.delete(schedule._id);
                          }
                          return { ...prev, schedules: Array.from(current) };
                        });
                      }}
                    />
                    <span>{schedule.name}</span>
                  </label>
                );
              })}
              {schedules.length === 0 && (
                <p className="text-xs text-gray-500">No schedules available. Create schedules first.</p>
              )}
            </div>
          </div>
          
          <Input label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter description (optional)" fullWidth />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editingSection ? 'Update Section' : 'Create Section'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SectionsPage;

